// Öffentliche Startseite.
//
// Erste Seite unter der Domain: erklärt das Produkt, zeigt Preise und führt
// zu Anmeldung oder Registrierung. Wer angemeldet ist, sieht stattdessen die
// Übersicht — dafür sorgt das Routing in App.tsx.
//
// Bewusst ohne Bilddateien: die App-Vorschau ist aus denselben Bausteinen
// gebaut wie die App selbst. So veraltet kein Screenshot, und die Seite lädt
// ohne zusätzliche Anfragen.
import { RECHTSLINKS } from '../../rechtliches/lib/links';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../gemeinsam/lib/api';
import { useThema } from '../../../gemeinsam/lib/theme';
import { ANBIETER_URL, POWERED_BY, PRODUKT } from '../../../gemeinsam/lib/marke';
import { SPRACHEN, useSprache, type SprachCode, type Schluessel } from '../../../gemeinsam/lib/i18n';
import Icon from '../../../gemeinsam/komponenten/Icon';
import { LogoKachel } from '../../firma/komponenten/Logo';
import type { IconName } from '../../../gemeinsam/komponenten/Icon';
import AppHerunterladen from '../komponenten/AppHerunterladen';

/**
 * Zahlenformat je Sprache. Für Arabisch werden bewusst lateinische Ziffern
 * erzwungen: im Geschäftsverkehr sind sie dort die Regel, und ein Preis in
 * arabisch-indischen Ziffern wäre neben dem Euro-Zeichen eher Stolperstein
 * als Entgegenkommen.
 */
const ZAHLFORMAT: Record<SprachCode, string> = {
  de: 'de-DE', en: 'en-GB', fr: 'fr-FR', es: 'es-ES',
  tr: 'tr-TR', ar: 'ar-u-nu-latn', zh: 'zh-CN',
};

type Tarif = {
  id: string;
  label: string;
  price: number;
  vat: number;
  description: string;
  features: string[];
  notIncluded: string[];
};

/** Tarifpreise: glatte Beträge ohne Nachkommastellen (19 €, nicht 19,00 €). */
const euro = (n: number, sprache: SprachCode) =>
  n.toLocaleString(ZAHLFORMAT[sprache] ?? 'de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Beträge in der App-Vorschau: immer zwei Nachkommastellen, wie in der App. */
const betrag = (n: number, sprache: SprachCode) =>
  n.toLocaleString(ZAHLFORMAT[sprache] ?? 'de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Inhalte stehen als Schlüsselpaare da, nicht als Text: so bleibt die Reihenfolge
// im Code sichtbar und die Sprache kommt aus der Übersetzungstabelle.
const FUNKTIONEN: Array<{ symbol: IconName; titel: Schluessel; text: Schluessel }> = [
  { symbol: 'assistent',   titel: 'start.f1t', text: 'start.f1x' },
  { symbol: 'lager',       titel: 'start.f2t', text: 'start.f2x' },
  { symbol: 'kasse',       titel: 'start.f3t', text: 'start.f3x' },
  { symbol: 'belege',      titel: 'start.f4t', text: 'start.f4x' },
  { symbol: 'buchhaltung', titel: 'start.f5t', text: 'start.f5x' },
  { symbol: 'retouren',    titel: 'start.f6t', text: 'start.f6x' },
  { symbol: 'chat',        titel: 'start.f7t', text: 'start.f7x' },
];

const KETTE: Array<{ kurz: Schluessel; text: Schluessel }> = [
  { kurz: 'start.kette1t', text: 'start.kette1x' },
  { kurz: 'start.kette2t', text: 'start.kette2x' },
  { kurz: 'start.kette3t', text: 'start.kette3x' },
  { kurz: 'start.kette4t', text: 'start.kette4x' },
];

const VERTRAUEN: Array<{ titel: Schluessel; text: Schluessel }> = [
  { titel: 'start.vertrauen1t', text: 'start.vertrauen1x' },
  { titel: 'start.vertrauen2t', text: 'start.vertrauen2x' },
  { titel: 'start.vertrauen3t', text: 'start.vertrauen3x' },
  { titel: 'start.vertrauen4t', text: 'start.vertrauen4x' },
];

export default function Start() {
  const { thema, setThema } = useThema();
  const { sprache } = useSprache();
  const [tarife, setTarife] = useState<Tarif[] | null>(null);

  useEffect(() => {
    // Preise kommen aus dem Tarif-Katalog des Servers, damit die Startseite
    // nie einen anderen Preis zeigt als die Abrechnung. Die Sprache geht mit:
    // die Leistungslisten liegen auf dem Server, nicht in der Übersetzungstabelle.
    setTarife(null);
    api<{ tarife: Tarif[] }>(`/oeffentlich/tarife?sprache=${sprache}`)
      .then(d => setTarife(d.tarife))
      .catch(() => setTarife([]));
  }, [sprache]);

  return (
    <div className="h-full overflow-y-auto bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <Kopfleiste thema={thema} setThema={setThema} />
      <Hero />
      <Vertrauensleiste />
      <Funktionen />
      <Belegkette />
      <Preise tarife={tarife} />
      <AlsApp />
      <Abschluss />
      <Fusszeile />
    </div>
  );
}

// ---------------------------------------------------------------------------

function Kopfleiste({ thema, setThema }: { thema: string; setThema: (t: 'hell' | 'dunkel' | 'system') => void }) {
  const { t } = useSprache();
  const [offen, setOffen] = useState(false);
  const naechstesThema = thema === 'dunkel' ? 'hell' : 'dunkel';

  const anker: Array<{ ziel: string; text: Schluessel }> = [
    { ziel: '#funktionen', text: 'start.nav.funktionen' },
    { ziel: '#belegkette', text: 'start.nav.belegkette' },
    { ziel: '#preise', text: 'start.nav.preise' },
    { ziel: '#app', text: 'start.nav.app' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/85 backdrop-blur dark:border-slate-800/80 dark:bg-slate-950/85">
      <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3.5 sm:px-6">
        <a href="#oben" className="flex items-center gap-2.5">
          <LogoKachel />
          <span className="text-lg font-bold tracking-tight">LagerPro</span>
        </a>

        <nav className="ms-4 hidden gap-1 md:flex">
          {anker.map(a => (
            <a key={a.ziel} href={a.ziel}
               className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white">
              {t(a.text)}
            </a>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <button
            onClick={() => setThema(naechstesThema)}
            aria-label={naechstesThema === 'dunkel' ? t('start.themaDunkel') : t('start.themaHell')}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <Icon name={naechstesThema === 'dunkel' ? 'mond' : 'sonne'} klasse="h-5 w-5" />
          </button>

          {/* Die Sprachwahl steht direkt links vom Zugang: wer die Seite nicht
              lesen kann, sucht sie genau dort, wo er sich anmelden will. */}
          <Sprachwahl />

          {/* Der Zugang zur App gehört nach oben rechts — dort wird er gesucht. */}
          <Link to="/anmelden"
                className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
            {t('aktion.anmelden')}
          </Link>
          <Link to="/registrieren"
                className="hidden rounded-lg bg-marke-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-marke-700 sm:block">
            {t('start.kostenlosStarten')}
          </Link>

          <button onClick={() => setOffen(o => !o)} aria-label={t('allg.menue')}
                  className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 md:hidden dark:text-slate-300 dark:hover:bg-slate-800">
            <Icon name={offen ? 'schliessen' : 'menue'} klasse="h-5 w-5" />
          </button>
        </div>
      </div>

      {offen && (
        <nav className="border-t border-slate-200 px-4 py-2 md:hidden dark:border-slate-800">
          {anker.map(a => (
            <a key={a.ziel} href={a.ziel} onClick={() => setOffen(false)}
               className="block rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800">
              {t(a.text)}
            </a>
          ))}
          <Link to="/registrieren" onClick={() => setOffen(false)}
                className="mt-1 block rounded-lg bg-marke-600 px-3 py-2.5 text-center text-sm font-semibold text-white">
            {t('start.kostenlosStarten')}
          </Link>
        </nav>
      )}
    </header>
  );
}

/**
 * Sprachwahl in der Kopfleiste.
 *
 * Bewusst ein echtes <select>: Es öffnet auf dem Handy die Auswahl des Systems,
 * lässt sich mit der Tastatur bedienen und braucht keine eigene Logik für
 * Schließen bei Klick daneben. Sichtbar ist nur die Flagge — der Name steht als
 * Beschriftung daneben, sobald Platz ist.
 */
function Sprachwahl() {
  const { t, sprache, setSprache } = useSprache();
  const aktuell = SPRACHEN.find(s => s.code === sprache) ?? SPRACHEN[0];

  return (
    <label className="relative flex h-9 items-center">
      <span className="sr-only">{t('start.sprache')}</span>
      <span aria-hidden
            className="pointer-events-none flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
        <span className="text-base leading-none">{aktuell.flagge}</span>
        <span className="hidden lg:inline">{aktuell.name}</span>
        <span className="text-[10px] text-slate-400">▾</span>
      </span>
      <select
        value={sprache}
        onChange={e => setSprache(e.target.value as SprachCode)}
        title={t('start.sprache')}
        className="absolute inset-0 cursor-pointer appearance-none rounded-lg bg-transparent text-transparent opacity-0 focus-visible:opacity-100 focus-visible:text-slate-900"
      >
        {SPRACHEN.map(sp => (
          <option key={sp.code} value={sp.code}>{sp.flagge} {sp.name}</option>
        ))}
      </select>
    </label>
  );
}

function Hero() {
  const { t } = useSprache();
  return (
    <section id="oben" className="relative overflow-hidden">
      {/* Farbschleier im Hintergrund — reine CSS-Verläufe, kein Bild. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 start-1/4 h-96 w-96 rounded-full bg-marke-400/25 blur-3xl dark:bg-marke-600/20" />
        <div className="absolute -top-24 end-0 h-80 w-80 rounded-full bg-emerald-300/25 blur-3xl dark:bg-emerald-700/15" />
      </div>

      <div className="mx-auto max-w-6xl px-4 pb-16 pt-16 sm:px-6 sm:pb-24 sm:pt-24">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {t('start.hero.marke')}
            </p>

            <h1 className="mt-5 text-4xl font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
              {t('start.hero.titel1')}
              <span className="block bg-gradient-to-r from-marke-600 to-emerald-600 bg-clip-text text-transparent dark:from-marke-400 dark:to-emerald-400">
                {t('start.hero.titel2')}
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              {t('start.hero.text')}
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/registrieren"
                    className="rounded-xl bg-marke-600 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-marke-600/20 transition hover:bg-marke-700">
                {t('start.kostenlosStarten')}
              </Link>
              <Link to="/anmelden"
                    className="rounded-xl border border-slate-300 px-6 py-3.5 text-base font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-900">
                {t('start.hero.konto')}
              </Link>
            </div>

            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {t('start.hero.fussnote')}
            </p>
          </div>

          <AppVorschau />
        </div>
      </div>
    </section>
  );
}

/** Stilisierte Ansicht der App — gebaut, nicht fotografiert. */
function AppVorschau() {
  const { t, sprache } = useSprache();
  const kacheln: Array<{ titel: Schluessel; wert: string; ton: string }> = [
    { titel: 'start.vorschau.umsatz',    wert: `${betrag(1284.5, sprache)} €`, ton: 'text-emerald-600 dark:text-emerald-400' },
    { titel: 'start.vorschau.offen',     wert: `${betrag(3940, sprache)} €`,   ton: 'text-slate-900 dark:text-slate-100' },
    { titel: 'start.vorschau.lagerwert', wert: `${euro(48210, sprache)} €`,    ton: 'text-slate-900 dark:text-slate-100' },
    { titel: 'start.vorschau.knapp',     wert: '2', ton: 'text-amber-600 dark:text-amber-400' },
  ];
  const zeilen: Array<[IconName, Schluessel]> = [
    ['kasse', 'nav.kasse'], ['belege', 'nav.belege'], ['rechnungen', 'nav.rechnungen'],
    ['lager', 'nav.lager'], ['buchhaltung', 'nav.buchhaltung'],
  ];
  const belege: Array<[string, Schluessel]> = [
    ['AN-202608-0004', 'start.vorschau.angebot'],
    ['LS-202608-0011', 'start.vorschau.lieferschein'],
    ['RE-202608-0031', 'start.vorschau.rechnung'],
  ];

  return (
    <div aria-hidden className="relative">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/40">
        <div className="flex items-center gap-1.5 border-b border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-950">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
          <span className="ms-3 truncate text-xs text-slate-400 dark:text-slate-500">lagerpro.app</span>
        </div>

        <div className="flex">
          <div className="hidden w-36 shrink-0 border-e border-slate-200 p-3 sm:block dark:border-slate-800">
            <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t('nav.gruppe.verkauf')}</p>
            {zeilen.map(([symbol, schluessel], i) => (
              <div key={schluessel} className={`mb-0.5 flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${i === 1
                ? 'bg-marke-50 font-medium text-marke-700 dark:bg-marke-950 dark:text-marke-300'
                : 'text-slate-500 dark:text-slate-400'}`}>
                <Icon name={symbol} klasse="h-3.5 w-3.5" />
                {t(schluessel)}
              </div>
            ))}
          </div>

          <div className="min-w-0 flex-1 p-4">
            <p className="text-sm font-semibold">{t('start.vorschau.gruss')}</p>
            <p className="text-[11px] text-slate-400">{t('start.vorschau.firma')}</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {kacheln.map(k => (
                <div key={k.titel} className="rounded-lg border border-slate-200 p-2.5 dark:border-slate-800">
                  <p className="truncate text-[10px] text-slate-400">{t(k.titel)}</p>
                  <p className={`mt-0.5 text-sm font-bold ${k.ton}`}>{k.wert}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg border border-slate-200 p-2.5 dark:border-slate-800">
              <p className="text-[10px] font-medium text-slate-400">{t('start.vorschau.letzte')}</p>
              {belege.map(([nr, art]) => (
                <div key={nr} className="mt-1.5 flex items-center justify-between text-[11px]">
                  <span className="font-mono text-slate-600 dark:text-slate-300">{nr}</span>
                  <span className="text-slate-400">{t(art)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Vertrauensleiste() {
  const { t } = useSprache();
  return (
    <section className="border-y border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
        {VERTRAUEN.map(v => (
          <div key={v.titel}>
            <p className="flex items-center gap-2 text-sm font-semibold">
              <span className="text-emerald-600 dark:text-emerald-400">✓</span>{t(v.titel)}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{t(v.text)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Funktionen() {
  const { t } = useSprache();
  return (
    <section id="funktionen" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="max-w-2xl">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('start.funktionen.titel')}</h2>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">{t('start.funktionen.text')}</p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {FUNKTIONEN.map(f => (
          <div key={f.titel}
               className="rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-marke-300 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-marke-700">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-marke-50 text-marke-600 dark:bg-marke-950 dark:text-marke-400">
              <Icon name={f.symbol} klasse="h-6 w-6" />
            </span>
            <h3 className="mt-4 text-lg font-semibold">{t(f.titel)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{t(f.text)}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Belegkette() {
  const { t } = useSprache();
  return (
    <section id="belegkette" className="border-y border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('start.kette.titel')}</h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">{t('start.kette.text')}</p>
        </div>

        <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {KETTE.map((k, i) => (
            <li key={k.kurz} className="relative rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
              <span className="text-xs font-bold text-marke-600 dark:text-marke-400">{t('start.kette.schritt')} {i + 1}</span>
              <p className="mt-1.5 text-lg font-semibold">{t(k.kurz)}</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t(k.text)}</p>
              {i < KETTE.length - 1 && (
                <span aria-hidden className="absolute -end-3 top-1/2 hidden -translate-y-1/2 text-2xl text-slate-300 lg:block dark:text-slate-700">
                  ›
                </span>
              )}
            </li>
          ))}
        </ol>

        <p className="mt-6 text-sm text-slate-500 dark:text-slate-400">
          {t('start.kette.fuss')}
        </p>
      </div>
    </section>
  );
}

function Preise({ tarife }: { tarife: Tarif[] | null }) {
  const { t, sprache } = useSprache();
  const empfohlen = 'professional';

  return (
    <section id="preise" className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="max-w-2xl">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('start.preise.titel')}</h2>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">{t('start.preise.text')}</p>
      </div>

      {tarife === null ? (
        <p className="mt-12 text-sm text-slate-500">{t('start.preise.laedt')}</p>
      ) : tarife.length === 0 ? (
        <p className="mt-12 text-sm text-slate-500">{t('start.preise.fehler')}</p>
      ) : (
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {tarife.map(tarif => {
            const top = tarif.id === empfohlen;
            return (
              <div key={tarif.id}
                   className={`relative flex flex-col rounded-2xl border p-7 ${top
                     ? 'border-marke-500 bg-white shadow-xl shadow-marke-600/10 dark:bg-slate-900'
                     : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'}`}>
                {top && (
                  <span className="absolute -top-3 start-7 rounded-full bg-marke-600 px-3 py-1 text-xs font-semibold text-white">
                    {t('start.preise.top')}
                  </span>
                )}
                <h3 className="text-lg font-bold">{tarif.label}</h3>
                <p className="mt-1 min-h-[2.5rem] text-sm text-slate-600 dark:text-slate-400">{tarif.description}</p>
                <p className="mt-4">
                  <span className="text-4xl font-bold">{euro(tarif.price, sprache)} €</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400"> {t('start.preise.proMonat')}</span>
                </p>

                <ul className="mt-6 flex-1 space-y-2.5">
                  {tarif.features.map(f => (
                    <li key={f} className="flex gap-2.5 text-sm text-slate-700 dark:text-slate-300">
                      <span className="mt-0.5 text-emerald-600 dark:text-emerald-400">✓</span>{f}
                    </li>
                  ))}
                  {tarif.notIncluded.map(f => (
                    <li key={f} className="flex gap-2.5 text-sm text-slate-400 dark:text-slate-500">
                      <span className="mt-0.5">–</span>{f}
                    </li>
                  ))}
                </ul>

                <Link to={`/registrieren?tarif=${tarif.id}`}
                      className={`mt-7 rounded-xl px-5 py-3 text-center text-sm font-semibold transition ${top
                        ? 'bg-marke-600 text-white hover:bg-marke-700'
                        : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'}`}>
                  {tarif.id === 'starter'
                    ? t('start.preise.sofort')
                    : t('start.preise.waehlen').replace('{tarif}', tarif.label)}
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** LagerPro als App auf dem Rechner — installieren oder herunterladen. */
function AlsApp() {
  const { t } = useSprache();
  return (
    <section id="app" className="border-y border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('desktop.titel')}</h2>
          <p className="mt-3 text-slate-600 dark:text-slate-400">{t('desktop.text')}</p>
        </div>
        <AppHerunterladen kompakt />
        <Link to="/download" className="mt-4 inline-block text-sm font-medium text-marke-700 hover:underline dark:text-marke-300">
          {t('desktop.mehrInfos')} →
        </Link>
      </div>
    </section>
  );
}

function Abschluss() {
  const { t } = useSprache();
  return (
    <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6">
      <div className="rounded-3xl bg-gradient-to-br from-marke-600 to-marke-800 px-8 py-14 text-center text-white sm:px-16">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t('start.ende.titel')}</h2>
        <p className="mx-auto mt-4 max-w-xl text-marke-100">{t('start.ende.text')}</p>
        <Link to="/registrieren"
              className="mt-8 inline-block rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-marke-700 shadow-lg transition hover:bg-marke-50">
          {t('start.kostenlosStarten')}
        </Link>
      </div>
    </section>
  );
}

function Fusszeile() {
  const { t } = useSprache();
  return (
    <footer className="border-t border-slate-200 dark:border-slate-800">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-8 text-sm text-slate-500 sm:px-6 dark:text-slate-400">
        <span className="font-semibold text-slate-700 dark:text-slate-200">{PRODUKT}</span>
        <span>© {new Date().getFullYear()}</span>
        <a href={ANBIETER_URL} target="_blank" rel="noopener noreferrer"
           className="hover:text-slate-900 hover:underline dark:hover:text-white">
          {POWERED_BY}
        </a>
        <a href="#preise" className="hover:text-slate-900 dark:hover:text-white">{t('start.nav.preise')}</a>
        <Link to="/anmelden" className="hover:text-slate-900 dark:hover:text-white">{t('aktion.anmelden')}</Link>
        {RECHTSLINKS.map(([ziel, name]) => (
          <Link key={ziel} to={ziel} className="hover:text-slate-900 dark:hover:text-white">{name}</Link>
        ))}
        <span className="ms-auto">{t('start.fuss.hinweis')}</span>
      </div>
    </footer>
  );
}
